import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useHomeApps } from '../services/homeApi';

/**
 * Map v2 service list rows to the { id, name } shape the dropdown needs.
 * Exported so it can be unit-tested independently of the component.
 */
export function normalizeDropdownApps(homeApps) {
  if (!Array.isArray(homeApps)) return [];
  return homeApps
    .filter(a => a?.app_id && a?.app_name)
    .map(a => ({ id: String(a.app_id), name: a.app_name }));
}

/**
 * AppSelector - Stacked label/value card with custom dropdown
 * Mock2-style: small uppercase label on top, bold value below, dropdown menu on click
 *
 * App list is sourced from /v2/data/services (same as Home cards) so the
 * dropdown always reflects the live v2 service inventory for the selected env/range.
 *
 * @param {boolean} forceDisabled - If true, selector is disabled regardless of route
 */
const AppSelector = ({ forceDisabled = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedEnvironment } = useEnvironment();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const { apps: homeApps, loading } = useHomeApps({ enabled: Boolean(selectedEnvironment) });
  const apps = useMemo(() => normalizeDropdownApps(homeApps), [homeApps]);

  // Extract current app ID from route /app/:selectedApp
  const currentAppId = useMemo(() => {
    const match = location.pathname.match(/^\/app\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleAppChange = (appId) => {
    if (!appId || forceDisabled) return;
    setIsOpen(false);

    // Extract page type after /app/:appId
    let pageSuffix = '';

    // Detail pages → navigate to new app overview
    if (location.pathname.match(/^\/app\/[^/]+\/traces\/[^/]+/) ||
        location.pathname.match(/^\/app\/[^/]+\/workflow\/[^/]+$/)) {
      pageSuffix = '';
    }
    // App-scoped pages
    else if (location.pathname.match(/^\/app\/[^/]+\/(cost|performance|sessions|traces|agents)$/)) {
      const match = location.pathname.match(/^\/app\/[^/]+\/(cost|performance|sessions|traces|agents)$/);
      pageSuffix = `/${match[1]}`;
    }
    // Workflow-scoped traces
    else if (location.pathname.match(/^\/app\/[^/]+\/workflow\/[^/]+\/traces$/)) {
      pageSuffix = '';
    }

    // Preserve env and range query params
    const params = new URLSearchParams();
    if (!pageSuffix) params.set('tab', 'overview');
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    const range = searchParams.get('range');
    if (range) params.set('range', range);

    navigate(`/app/${encodeURIComponent(appId)}${pageSuffix}?${params.toString()}`);
  };

  const currentApp = apps.find(a => a.id === currentAppId);
  const displayName = currentApp?.name || (currentAppId ? currentAppId : 'Select app');
  const isDisabled = forceDisabled || apps.length === 0;

  if (loading) {
    return (
      <div className="relative flex flex-col items-start">
        <span className="block text-[10px] text-text-secondary uppercase tracking-wide mb-1 pl-3">App</span>
        <div className="px-3 py-2 rounded-xl bg-background-tertiary min-w-[150px]">
          <span className="text-sm text-text-muted leading-snug">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-start" ref={ref}>
      <span className="block text-[10px] text-text-secondary uppercase tracking-wide mb-1 pl-3">App</span>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-tertiary min-w-[150px] transition-colors ${
          isDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:bg-background-quaternary'
        }`}
      >
        <span className="truncate max-w-[160px] text-sm font-semibold text-text-primary leading-snug">{displayName}</span>
        {!isDisabled && (
          <ChevronDown size={12} className={`text-text-muted flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && apps.length > 0 && (
        <div
          className="absolute left-0 top-[calc(100%+6px)] w-full min-w-[180px] rounded-xl p-1.5 z-50 max-h-[300px] overflow-y-auto"
          style={{
            backgroundColor: 'var(--color-panel-secondary)',
            border: '1px solid var(--color-border-strong)',
            boxShadow: 'var(--shadow-dropdown)',
          }}
        >
          {apps.map((app) => (
            <button
              type="button"
              key={app.id}
              onClick={() => handleAppChange(app.id)}
              className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition-colors ${
                currentAppId === app.id
                  ? 'text-accent-primary-base font-medium'
                  : 'text-text-primary hover:bg-background-hover'
              }`}
            >
              {app.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppSelector;
